// @ts-check

import React from 'react';

import ReactDOM from 'react-dom';

import { defaultFeatureFlags } from '@af/design-system-docs-ui';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';

import ErrorBoundary from './src/__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/error-boundary';
import AnalyticsProvider from './src/analytics/analytics-provider';
import GlobalError from './src/components/global-error';

/**
 * @type {import('gatsby').GatsbyBrowser['wrapRootElement']}
 */
export const wrapRootElement = ({ element }) => {
	return (
		<AnalyticsProvider>
			<ErrorBoundary fallback={<GlobalError />}>{element}</ErrorBoundary>
		</AnalyticsProvider>
	);
};

export { wrapPageElement } from './src/utils/wrap-page-element';

/**
 * @type {import('gatsby').GatsbyBrowser['onClientEntry']}
 */
export const onClientEntry = () => {
	// This resolver is later overwritten in the Feature Flag Provider,
	// but needs to initialize here first because Gatsby is too slow
	// to hydrate otherwise.
	setBooleanFeatureFlagResolver((flagKey) => {
		return defaultFeatureFlags[flagKey] || false;
	});

	try {
		// Add a polyfill for Promise.allSettled
		if (Promise && !Promise.allSettled) {
			Promise.allSettled = (promises) => {
				return Promise.all(
					promises.map((promise) => {
						return promise
							.then((value) => {
								return { state: 'fulfilled', value };
							})
							.catch((reason) => {
								return { state: 'rejected', reason };
							});
					}),
				);
			};
		}
	} catch (err) {
		console.error(err);
	}
};

export const onInitialClientRender = async () => {
	if (process.env.NODE_ENV === 'development' && process.env.GATSBY_AXE_ENABLED === 'TRUE') {
		const { default: axe } = await import(
			/* webpackChunkName: "@atlaskit-internal_axe-core-for-dev-env" */
			'@axe-core/react'
		);
		axe(React, ReactDOM, 1000, {});
	}
};

export const shouldUpdateScroll = () => {
	/**
	 * This is a workaround for broken scroll behaviour on route changes.
	 *
	 * Due to our nav, only the main-content actually scrolls, not the `body`
	 * element. This means that even when scrolled to the bottom of the page,
	 * `window.scrollPosition === [0, 0]`, which means that Gatsby doesn't set the
	 * scroll position of the correct element on route changes.
	 *
	 * `shouldUpdateScroll` is fired on every route change, so we can grab the main
	 * content element here and scroll it ourselves.
	 */
	document.getElementById('main-content')?.scrollTo(0, 0);
};
